import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Download, Loader2, Save, Upload } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPricing() {
  const [cabinets, setCabinets] = useState([]);
  const [products, setProducts] = useState([]);
  const [cabinetId, setCabinetId] = useState('');
  const [grid, setGrid] = useState(null);
  const [prices, setPrices] = useState({});
  const [params, setParams] = useState({ discount_pct: 0, markup_pct: 0, coefficient: 1, vat_rate: 20 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([axios.get(`${API}/organizations?type=cabinet`), axios.get(`${API}/products`)])
      .then(([c, p]) => { setCabinets(c.data); setProducts(p.data); })
      .catch(() => toast.error('Erreur de chargement'));
  }, []);

  const loadGrid = useCallback(async (cid) => {
    setCabinetId(cid);
    try {
      const { data } = await axios.get(`${API}/price-grids`, { params: { cabinet_id: cid } });
      const g = data[0] || null;
      setGrid(g);
      setPrices(g ? Object.fromEntries(g.lines.map((l) => [l.product_id, l.price])) : {});
      setParams(g ? { discount_pct: g.discount_pct, markup_pct: g.markup_pct, coefficient: g.coefficient, vat_rate: g.vat_rate } : { discount_pct: 0, markup_pct: 0, coefficient: 1, vat_rate: 20 });
    } catch (e) {
      toast.error('Erreur de chargement de la grille');
    }
  }, []);

  const save = async () => {
    const lines = Object.entries(prices)
      .filter(([, v]) => v !== '' && !Number.isNaN(parseFloat(v)))
      .map(([product_id, price]) => ({ product_id, price: parseFloat(price) }));
    if (!lines.length) { toast.error('Renseignez au moins un prix'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/price-grids`, { cabinet_id: cabinetId, lines, ...params });
      toast.success(`Grille v${data.version} enregistrée et activée`);
      loadGrid(cabinetId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    } finally { setBusy(false); }
  };

  const importExcel = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/price-grids/parse-excel`, fd);
      setPrices((p) => ({ ...p, ...Object.fromEntries(data.lines.map((l) => [l.product_id, l.price])) }));
      toast.success(`${data.lines.length} prix importés — vérifiez puis enregistrez`);
      if (data.errors.length) toast.warning(data.errors.join(' · '));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'import Excel");
    }
  };

  return (
    <AdminLayout title="Grilles tarifaires" subtitle="Prix négociés par cabinet — jamais visibles des laboratoires">
      <div className="max-w-2xl space-y-4">
        <div>
          <Label>Cabinet</Label>
          <Select value={cabinetId} onValueChange={loadGrid}>
            <SelectTrigger data-testid="pricing-cabinet-select"><SelectValue placeholder="Choisir un cabinet..." /></SelectTrigger>
            <SelectContent>{cabinets.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {cabinetId && (
          <Card className="border-border">
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">
                {grid ? `Grille active v${grid.version}` : 'Nouvelle grille (aucune grille active)'}
              </CardTitle>
              <div className="flex gap-2">
                {grid && (
                  <a href={`${API}/price-grids/${grid.id}/export`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" className="gap-1" data-testid="pricing-export-btn"><Download className="h-4 w-4" />Excel</Button>
                  </a>
                )}
                <label>
                  <Button size="sm" variant="outline" className="gap-1" asChild>
                    <span className="cursor-pointer"><Upload className="h-4 w-4" />Importer</span>
                  </Button>
                  <input type="file" accept=".xlsx" className="hidden" onChange={(e) => importExcel(e.target.files[0])} data-testid="pricing-import-input" />
                </label>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="divide-y divide-border">
                {products.map((p) => (
                  <div key={p.id} className="py-2 flex items-center justify-between gap-3">
                    <span className="text-sm">{p.label_fr} <span className="text-xs text-muted-foreground">({p.code})</span></span>
                    <div className="flex items-center gap-1">
                      <Input type="number" step="0.01" className="w-28 text-right" value={prices[p.id] ?? ''}
                        onChange={(e) => setPrices({ ...prices, [p.id]: e.target.value })}
                        data-testid={`price-input-${p.code}`} />
                      <span className="text-sm text-muted-foreground">€</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-4 gap-3">
                {[['discount_pct', 'Remise %'], ['markup_pct', 'Majoration %'], ['coefficient', 'Coefficient'], ['vat_rate', 'TVA %']].map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-xs">{label}</Label>
                    <Input type="number" step="0.01" value={params[k]}
                      onChange={(e) => setParams({ ...params, [k]: parseFloat(e.target.value) || 0 })}
                      data-testid={`pricing-param-${k}`} />
                  </div>
                ))}
              </div>
              <Button onClick={save} disabled={busy} className="gap-2" data-testid="pricing-save-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Enregistrer (nouvelle version)
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
